import { TYPE_ENABLED, canSelectEquipment } from '#constants';

/** Local calendar day at midnight — matches DateRangeSelector's past-date guard. */
function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Shared validation for a tracker's date range and equipment selection.
 *
 * Used by both the create flow (TrackWidget) and the edit flow (EditTracker)
 * so the two can never silently diverge. Keeping this in one place is what
 * prevents bugs like a hotel tracker (e.g. Phantom Ranch) being saved with the
 * same start and end date: hotels are two-date outings but are not
 * equipment-selectable, so an equipment-keyed guard would miss them.
 *
 * @param {Object} param0
 * @param {Object} param0.outing - The outing being tracked
 * @param {Date|null} param0.startDate
 * @param {Date|null} param0.endDate
 * @param {string} [param0.equipmentType]
 * @param {Date} [param0.now] - Injectable "now" for testing; defaults to new Date()
 * @returns {{ date?: string|null, equipmentType?: string|null }} error map
 *   (a falsy value means no error for that field)
 */
export function getTrackerDateErrors({
  outing,
  startDate,
  endDate,
  equipmentType,
  now = new Date(),
}) {
  const errors = {};
  const twoDates = TYPE_ENABLED.twoDates.includes(outing?.entity_type);

  if (twoDates) {
    if (!startDate || !endDate) {
      errors.date = 'Select both checkin and checkout dates';
    } else if (startDate - endDate >= 0) {
      // Catches same-day (start === end) and inverted ranges (start > end).
      errors.date = 'Checkout should be atleast a day after checkin';
    } else if (startOfLocalDay(startDate) < startOfLocalDay(now)) {
      errors.date = 'Checkin date cannot be in the past';
    } else {
      errors.date = null;
    }
  } else {
    if (!startDate) {
      errors.date = 'Select a checkin date';
    } else if (startOfLocalDay(startDate) < startOfLocalDay(now)) {
      errors.date = 'Checkin date cannot be in the past';
    } else {
      errors.date = null;
    }
  }

  if (canSelectEquipment(outing)) {
    errors.equipmentType = equipmentType
      ? null
      : 'Equipment type should be selected';
  }

  return errors;
}
